import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { api } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";
import { StepTitle, ErrorState, Skeleton, PrimaryButton } from "@/components/BookingShell";
import { formatFullDate, toISODate } from "@/lib/format";

export function DateStep({ setFooter }) {
    const { service, date, setDate, setTime, next, cart } = useBooking();
    const [available, setAvailable] = useState(null); // Set of "YYYY-MM-DD"
    const [error, setError] = useState(null);
    const [rangeStart] = useState(() => new Date());
    const rangeEnd = useMemo(() => {
        const d = new Date(rangeStart);
        d.setDate(d.getDate() + 60);
        return d;
    }, [rangeStart]);

    useEffect(() => {
        if (!service?.id) return;
        setError(null);
        setAvailable(null);
        const request = service?.cartQuote ? api.cartAvailability(cart.map((c) => ({service_id:c.service.id, quantity:c.quantity})), toISODate(rangeStart), toISODate(rangeEnd)) : api.availableDates({
                serviceId: service.id,
                startDate: toISODate(rangeStart),
                endDate: toISODate(rangeEnd),
            });
        let active = true;
        request.then((res) => { if (active) setAvailable(new Set(res.dates || [])); })
            .catch((e) => { if (active) setError(e); });
        return () => { active = false; };
    }, [service, cart, rangeStart, rangeEnd]);

    const selectedDate = date ? new Date(`${date}T00:00:00`) : undefined;

    const isDisabled = (d) => {
        const iso = toISODate(d);
        // Disable past dates
        const today = toISODate(new Date());
        if (iso < today) return true;
        if (!available) return true;
        return !available.has(iso);
    };

    useEffect(() => {
        setFooter(
            date && available?.has(date) ? (
                <PrimaryButton onClick={next} testId="date-continue-button">Continue</PrimaryButton>
            ) : null
        );
        return () => setFooter(null);
    }, [date, available, next, setFooter]);

    return (
        <div>
            <StepTitle
                eyebrow={service?.name}
                title={<>Pick a <em className="italic gold-text not-italic font-serif">date</em></>}
                subtitle="Dates with availability are highlighted."
                testId="date-step-title"
            />

            {error && <ErrorState error={error} onRetry={() => window.location.reload()} />}

            <div className="rounded-3xl glass p-3 sm:p-5 animate-fade-up" data-testid="date-picker">
                {!available && !error ? (
                    <Skeleton className="h-[360px]" />
                ) : (
                    <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(d) => {
                            if (!d) return;
                            setDate(toISODate(d));
                            setTime(null);
                        }}
                        disabled={isDisabled}
                        fromDate={rangeStart}
                        toDate={rangeEnd}
                        showOutsideDays={false}
                        className="mx-auto"
                    />
                )}
            </div>

            {date && (
                <div className="mt-6 text-center font-serif text-lg text-white/85" data-testid="date-selected">
                    {formatFullDate(date)}
                </div>
            )}
        </div>
    );
}
